import { useState } from 'react';
import { useSettings, useUpdateSettings } from '../api/hooks';
import { formatDate, formatOffset, fromLocalInput } from '../lib/format';
import { Badge, Button, Card, ErrorBox, Field, Input, PageHeader, Section, SectionTitle, Spinner } from '../components/ui';
import { useToast } from '../components/toast';

export default function Horloge() {
  const toast = useToast();
  const { data: settings, isPending, error } = useSettings({ refetchInterval: 1000 });
  const update = useUpdateSettings();
  const [draft, setDraft] = useState<string | null>(null);
  const [target, setTarget] = useState('');

  const offset = settings?.clockOffsetSeconds ?? 0;

  const apply = (clockOffsetSeconds: number) =>
    update.mutate(
      { clockOffsetSeconds },
      {
        onSuccess: () => {
          setDraft(null);
          toast.success(`Décalage réglé à ${formatOffset(clockOffsetSeconds)}`);
        },
        onError: (err) => toast.error(err),
      },
    );

  if (error) return <ErrorBox error={error} />;
  if (isPending || !settings) return <Spinner />;

  return (
    <div className="space-y-5">
      <PageHeader title="Horloge serveur" />

      <Card tone="surface">
        <SectionTitle>Heure vue par l’app (en-tête Date)</SectionTitle>
        <p className="mt-2 font-mono text-2xl/7 font-bold tracking-[0.48px] text-white">
          {formatDate(settings.serverNow)}
        </p>
        <p className="mt-3 flex flex-wrap items-center gap-2 text-sm text-on-surface-variant">
          Décalage courant :{' '}
          <Badge variant={offset !== 0 ? 'ganymede' : 'outline'}>
            {formatOffset(offset)} ({offset} s)
          </Badge>
        </p>
      </Card>

      <Section title="Réglage du décalage">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Décalage en secondes">
            <Input
              type="number"
              aria-label="Décalage en secondes"
              value={draft ?? String(offset)}
              onChange={(e) => setDraft(e.target.value)}
            />
          </Field>
          <Button
            size="sm"
            variant="primary"
            className="mb-1"
            disabled={update.isPending}
            onClick={() => apply(Number(draft ?? offset))}
          >
            Appliquer
          </Button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" disabled={update.isPending} onClick={() => apply(offset + 3600)}>
            +1 h
          </Button>
          <Button size="sm" disabled={update.isPending} onClick={() => apply(offset + 86400)}>
            +1 j
          </Button>
          <Button size="sm" disabled={update.isPending} onClick={() => apply(offset - 3600)}>
            -1 h
          </Button>
          <Button size="sm" variant="ghost" disabled={update.isPending} onClick={() => apply(0)}>
            Remise à zéro
          </Button>
        </div>
      </Section>

      <Section title="Aller à une date">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Date cible" hint="Le décalage devient cible − heure réelle">
            <Input
              type="datetime-local"
              aria-label="Date cible"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            />
          </Field>
          <Button
            size="sm"
            variant="primary"
            disabled={update.isPending || !target}
            onClick={() => {
              const iso = fromLocalInput(target);
              if (!iso) return;
              apply(Math.round((new Date(iso).getTime() - Date.now()) / 1000));
            }}
          >
            Aller à
          </Button>
        </div>
      </Section>
    </div>
  );
}
